import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  BackendResponse,
  ContestViewContest,
  ContestViewEntry,
  CONTEST_ENTRY_AWARD_STATUSES,
  CONTEST_ENTRY_IMAGE_FORMATS,
  CONTEST_ENTRY_VIDEO_FORMATS,
  Datastore,
  DatastoreCollection,
  EntryAction,
  toNumber,
  User,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import {
  RibbonColor,
  RibbonOrientation,
  RibbonPlacement,
} from '@freelancer/ui/ribbon';
import { Tab } from '@freelancer/ui/tabs';
import {
  FontColor,
  FontSize,
  FontWeight,
  TextTransform,
} from '@freelancer/ui/text';
import { ThumbnailViewerImage } from '@freelancer/ui/thumbnail-viewer';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { EntryStatusApi } from 'api-typings/contests/contests';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { PreviewModalResponse } from './preview-modal.types';

@Component({
  selector: 'app-preview-modal',
  template: `
    <fl-link class="EntryPreview-navigator EntryPreview-navigator--previous">
      <fl-icon
        [name]="'ui-chevron-left'"
        [color]="IconColor.LIGHT"
        [size]="IconSize.LARGE"
      ></fl-icon>
    </fl-link>

    <fl-link class="EntryPreview-navigator EntryPreview-navigator--next">
      <fl-icon
        [name]="'ui-chevron-right'"
        [color]="IconColor.LIGHT"
        [size]="IconSize.LARGE"
      ></fl-icon>
    </fl-link>

    <fl-grid class="EntryPreview-layout">
      <fl-col class="EntryPreview-layout--primary" [col]="8">
        <fl-bit class="EntryPreview-header">
          <fl-bit
            class="EntryPreview-header-content"
            [flMarginRight]="Margin.XLARGE"
          >
            <fl-bit class="EntryPreview-header-content-title">
              <fl-heading
                i18n="Entry number header"
                [size]="HeadingSize.SMALL"
                [headingType]="HeadingType.H1"
                [flMarginRight]="Margin.XXSMALL"
              >
                Entry #{{ entry.number }}
              </fl-heading>
              <fl-icon
                *ngIf="isSealed()"
                [name]="'ui-lock-closed'"
                [size]="IconSize.SMALL"
                [color]="IconColor.INHERIT"
              ></fl-icon>
            </fl-bit>

            <fl-text
              i18n="Entry attachments text"
              [size]="FontSize.XSMALL"
              [color]="FontColor.MID"
              [textTransform]="TextTransform.UPPERCASE"
            >
              Attachments
            </fl-text>
            <fl-thumbnail-viewer
              class="EntryPreview-header-content-thumbnails"
              [images]="thumbnails"
              [slidesToShow]="THUMBNAIL_LIMIT"
              (slideChange)="onSlideChange($event)"
            ></fl-thumbnail-viewer>
          </fl-bit>

          <fl-bit class="EntryPreview-header-cta">
            <fl-rating
              [control]="ratingControl"
              [hideValue]="true"
              [readOnly]="!isContestOwner || (isRejected$ | async)"
            ></fl-rating>

            <ng-container
              *ngIf="
                showErrorsOnHeader() && (updateResponse$ | async) as response
              "
            >
              <app-entry-update-error
                *ngIf="response.errorCode"
                [flMarginBottom]="Margin.XXXSMALL"
                [response]="response"
                [source]="entryAction"
                [entry]="entry$ | async"
              ></app-entry-update-error>
            </ng-container>

            <fl-bit
              class="EntryPreview-header-cta-group"
              *ngIf="isContestOwner"
              [flMarginBottom]="Margin.XXSMALL"
            >
              <ng-container *ngIf="(isActive$ | async)">
                <fl-button
                  class="EntryPreview-header-cta-button"
                  i18n="Entry reject button"
                  [color]="ButtonColor.DEFAULT"
                  [size]="ButtonSize.SMALL"
                  [busy]="rejectResponse$ && (rejectResponse$ | async) === null"
                  [flMarginRight]="Margin.SMALL"
                  (click)="onRejectEntry(REJECT_ENTRY_CONTENTS)"
                >
                  Reject
                </fl-button>
                <fl-button
                  class="EntryPreview-header-cta-button"
                  i18n="Entry award button"
                  [color]="ButtonColor.SUCCESS"
                  [size]="ButtonSize.SMALL"
                  [disabled]="
                    rejectResponse$ && (rejectResponse$ | async) === null
                  "
                  (click)="onAwardEntry()"
                >
                  Award
                </fl-button>
              </ng-container>

              <fl-button
                class="EntryPreview-header-cta-button"
                *ngIf="(isRejected$ | async)"
                i18n="Entry view handover button"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [busy]="
                  reconsiderResponse$ && (reconsiderResponse$ | async) === null
                "
                (click)="onReconsiderEntry(RECONSIDER_ENTRY_CONTENTS)"
              >
                Reconsider
              </fl-button>

              <fl-button
                class="EntryPreview-header-cta-button"
                *ngIf="isAwarded() || isBought()"
                i18n="Entry view handover button"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [link]="'/contest/handover.php'"
                [queryParams]="{ entry_id: entry.id }"
              >
                View Handover
              </fl-button>

              <fl-button
                class="EntryPreview-header-cta-button"
                *ngIf="isBeingSold()"
                i18n="Entry buy button"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [link]="'/contest/' + contest.id + '-byentry-' + entry.id"
              >
                Buy
              </fl-button>
            </fl-bit>
          </fl-bit>
        </fl-bit>
        <fl-bit class="EntryPreview-file-container">
          <fl-bit
            class="EntryPreview-file-overlay"
            *ngIf="(isRejected$ | async)"
          >
            <fl-text
              class="EntryPreview-file-overlay-text"
              i18n="Entry rejected text"
              [color]="FontColor.LIGHT"
              [weight]="FontWeight.BOLD"
            >
              Rejected
            </fl-text>
          </fl-bit>
          <fl-ribbon
            class="EntryPreview-file-ribbon"
            *ngIf="isAwarded()"
            iconLabel="Awarded"
            [color]="RibbonColor.SECONDARY"
            [icon]="'ui-trophy'"
            [orientation]="RibbonOrientation.VERTICAL"
            [placement]="RibbonPlacement.LEFT"
          ></fl-ribbon>
          <fl-ribbon
            class="EntryPreview-file-ribbon"
            *ngIf="isBought()"
            iconLabel="Bought"
            [color]="RibbonColor.SECONDARY_DARK"
            [icon]="'ui-bought-medal'"
            [orientation]="RibbonOrientation.VERTICAL"
            [placement]="RibbonPlacement.LEFT"
          ></fl-ribbon>
          <fl-ribbon
            class="EntryPreview-file-ribbon"
            *ngIf="(isHighlighted$ | async)"
            iconLabel="Highlighted"
            i18n="Highlighted text"
            [color]="RibbonColor.SECONDARY"
            [placement]="RibbonPlacement.LEFT"
          >
            Highlighted
          </fl-ribbon>
          <fl-bit class="EntryPreview-file-content">
            <fl-bit
              class="EntryPreview-file-imageContainer"
              *ngIf="isImage(entry.files[currentFileIndex]?.name)"
            >
              <fl-picture
                alt="{{ entry.files[currentFileIndex]?.name }}"
                [src]="entry.files[currentFileIndex]?.thumbnail900Url"
                [externalSrc]="true"
                [display]="PictureDisplay.BLOCK"
                [boundedWidth]="true"
              ></fl-picture>
            </fl-bit>
            <fl-video
              class="EntryPreview-file-video"
              *ngIf="isVideo(entry.files[currentFileIndex]?.name)"
              [src]="entry.files[currentFileIndex]?.fileUrl"
            ></fl-video>
          </fl-bit>
        </fl-bit>
      </fl-col>
      <fl-col class="EntryPreview-layout--secondary" [col]="4">
        <fl-bit class="EntryPreview-details">
          <fl-bit
            class="EntryPreview-details-user"
            [ngClass]="{
              'EntryPreview-details-user--centerAlign': isContestOwner
            }"
            [flMarginBottom]="Margin.SMALL"
          >
            <fl-user-avatar
              [users]="[freelancer]"
              [size]="AvatarSize.LARGE"
              [flMarginRight]="Margin.XSMALL"
            ></fl-user-avatar>
            <fl-bit>
              <fl-bit
                class="EntryPreview-details-user-info"
                [flMarginBottom]="Margin.XXSMALL"
              >
                <!-- TODO: Fix logic on T81708  -->
                <fl-flag
                  *ngIf="false"
                  [country]="country"
                  [flMarginRight]="Margin.XXSMALL"
                ></fl-flag>
                <fl-text [weight]="FontWeight.BOLD">
                  {{ freelancer.displayName }}
                </fl-text>
              </fl-bit>
              <fl-button
                *ngIf="isContestOwner"
                i18n="Chat button"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.MINI"
                [flMarginBottom]="Margin.XXXSMALL"
                (click)="handleChatClick()"
              >
                <fl-online-indicator
                  [isOnline]="
                    freelancer.onlineOfflineStatus?.status === 'online'
                  "
                  [flMarginRight]="Margin.XXXSMALL"
                ></fl-online-indicator>
                Chat
              </fl-button>
            </fl-bit>
          </fl-bit>

          <fl-text
            class="EntryPreview-details-title"
            [weight]="FontWeight.BOLD"
            [flMarginBottom]="Margin.XSMALL"
          >
            {{ entry.title }}
          </fl-text>
          <fl-text
            class="EntryPreview-details-description"
            [flMarginBottom]="Margin.SMALL"
          >
            <fl-truncated-text
              [text]="entry.description"
              [limit]="DESCRIPTION_LIMIT"
            ></fl-truncated-text>
          </fl-text>

          <fl-bit
            class="EntryPreview-details-feedback"
            [flMarginBottom]="Margin.XXSMALL"
          >
            <ng-container *ngIf="(entry$ | async) as entryDetails">
              <fl-icon
                *ngIf="
                  !isEntryOwner() && !(isRejected$ | async);
                  else disableLike
                "
                class="EntryPreview-details-feedback-like"
                [name]="'ui-thumbs-up'"
                [color]="
                  entryDetails.isLiked ? IconColor.PRIMARY : IconColor.MID
                "
                [size]="IconSize.SMALL"
                [flMarginRight]="Margin.XXXSMALL"
                (click)="onLikeEntry({ isLiked: !entryDetails.isLiked })"
              ></fl-icon>
              <ng-template #disableLike>
                <fl-icon
                  [name]="'ui-thumbs-up'"
                  [color]="
                    entryDetails.isLiked ? IconColor.PRIMARY : IconColor.MID
                  "
                  [size]="IconSize.SMALL"
                  [flMarginRight]="Margin.XXXSMALL"
                ></fl-icon>
              </ng-template>
              <fl-text [flMarginRight]="Margin.XSMALL">
                {{ entryDetails.likeCount || 0 }}
              </fl-text>
            </ng-container>
            <fl-icon
              [name]="'ui-community'"
              [color]="IconColor.MID"
              [size]="IconSize.SMALL"
              [flMarginRight]="Margin.XXXSMALL"
            ></fl-icon>
            <fl-text> {{ entry.commentCount }} </fl-text>
          </fl-bit>

          <ng-container
            *ngIf="
              !showErrorsOnHeader() && (updateResponse$ | async) as response
            "
          >
            <app-entry-update-error
              *ngIf="response.errorCode"
              [flMarginBottom]="Margin.XXXSMALL"
              [response]="response"
              [source]="entryAction"
              [entry]="entry$ | async"
            ></app-entry-update-error>
          </ng-container>
        </fl-bit>
        <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
        <fl-bit class="EntryPreview-tab" [flMarginBottom]="Margin.SMALL">
          <!-- TODO: User fl-tabs when annotation tab is implemented -->
          <fl-tabs
            *ngIf="false"
            [tabs]="tabs"
            [flMarginBottom]="Margin.SMALL"
          ></fl-tabs>
          <fl-heading
            i18n="Comments heading"
            [flMarginBottom]="Margin.SMALL"
            [headingType]="HeadingType.H1"
            [size]="HeadingSize.XXSMALL"
          >
            Comments
          </fl-heading>
        </fl-bit>
        <fl-bit class="EntryPreview-comments">
          <app-comment-section
            [contest]="contest"
            [user]="user$ | async"
            [commentsCollection]="commentsCollection"
            [commentLimit]="commentLimit$ | async"
            [entry]="entry"
            [isQuickView]="true"
            (viewMoreComments)="onViewMoreComments()"
          ></app-comment-section>
        </fl-bit>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewModalComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FileDisplaySize = FileDisplaySize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  RibbonColor = RibbonColor;
  RibbonOrientation = RibbonOrientation;
  RibbonPlacement = RibbonPlacement;
  TextTransform = TextTransform;

  @Input() contest: ContestViewContest;
  @Input() entry: ContestViewEntry;
  @Input() freelancer: User;
  @Input() userId: number;
  @Input() isContestOwner: boolean;
  @Input() hasContestAwardedEntry: boolean;

  readonly THUMBNAIL_LIMIT = 5;
  readonly REJECT_ENTRY_CONTENTS = { status: EntryStatusApi.ELIMINATED };
  readonly RECONSIDER_ENTRY_CONTENTS = { status: EntryStatusApi.ACTIVE };
  readonly DESCRIPTION_LIMIT = 200;

  entry$: Rx.Observable<ContestViewEntry>;
  isHighlighted$: Rx.Observable<boolean>;
  isActive$: Rx.Observable<boolean>;
  isRejected$: Rx.Observable<boolean>;

  ratingControl = new FormControl();
  ratingSubscription: Rx.Subscription;
  thumbnails: ReadonlyArray<ThumbnailViewerImage> = [];
  currentFileIndex = 0;

  entryAction: EntryAction;
  updateResponse$: Promise<BackendResponse>;
  rejectResponse$: Promise<BackendResponse>;
  reconsiderResponse$: Promise<BackendResponse>;

  tabs: ReadonlyArray<Tab> = [
    {
      title: 'Comments',
      selected: true,
    },
  ];

  commentsCollection: DatastoreCollection<'contestEntryMessages'>;
  readonly COMMENT_LIMIT_INCREMENT = 50;
  private commentLimitSubject$ = new Rx.BehaviorSubject<number>(
    this.COMMENT_LIMIT_INCREMENT,
  );
  commentLimit$ = this.commentLimitSubject$.asObservable();
  user$: Rx.Observable<User>;

  /**
   * Placeholder variables.
   * TODO: Replace variables with dynamic data -- hooked from the backend.
   */
  country = 'au';

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private modalRef: ModalRef<PreviewModalComponent>,
  ) {}

  ngOnInit() {
    // Datastore call needed to have the entry status change reflected on the modal upon user CTA
    this.entry$ = this.datastore
      .document('contestViewEntries', this.entry.id)
      .valueChanges();

    this.isHighlighted$ = this.entry$.pipe(
      map(
        entry =>
          !!(
            this.entry.upgrades &&
            this.entry.upgrades.highlight &&
            !this.hasContestAwardedEntry &&
            ![
              EntryStatusApi.ELIMINATED,
              EntryStatusApi.WITHDRAWN,
              EntryStatusApi.WITHDRAWN_ELIMINATED,
            ].includes(entry.status)
          ),
      ),
    );

    this.isActive$ = this.entry$.pipe(
      map(
        entry =>
          !this.hasContestAwardedEntry &&
          entry.status === EntryStatusApi.ACTIVE,
      ),
    );

    this.isRejected$ = this.entry$.pipe(
      map(entry =>
        [
          EntryStatusApi.ELIMINATED,
          EntryStatusApi.WITHDRAWN_ELIMINATED,
        ].includes(entry.status),
      ),
    );

    this.ratingControl.setValue(this.entry.rating);

    this.ratingSubscription = this.ratingControl.valueChanges.subscribe(
      rating => this.onRateEntry({ rating }),
    );

    this.thumbnails = this.entry.files.map(file => ({
      src: file.thumbnail80Url || '',
      alt: ' ',
    }));

    this.commentsCollection = this.datastore.collection(
      'contestEntryMessages',
      query =>
        query.where('entryId', '==', this.entry.id).limit(this.commentLimit$),
    );

    const userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    this.user$ = this.datastore.document('users', userId$).valueChanges();
  }

  onAwardEntry() {
    // TODO: Open award modal directly in preview modal after T82677 is done
    this.modalRef.close(PreviewModalResponse.AWARD);
  }

  onSlideChange(index: number) {
    this.currentFileIndex = index;
  }

  onViewMoreComments() {
    this.commentLimitSubject$.next(
      this.commentLimitSubject$.getValue() + this.COMMENT_LIMIT_INCREMENT,
    );
  }

  isSealed() {
    return this.entry.upgrades && this.entry.upgrades.sealed;
  }

  isAwarded() {
    return CONTEST_ENTRY_AWARD_STATUSES.includes(this.entry.status);
  }

  isBought() {
    return this.entry.status === EntryStatusApi.BOUGHT;
  }

  isBeingSold() {
    return (
      this.hasContestAwardedEntry &&
      this.entry.status === EntryStatusApi.ACTIVE &&
      (this.entry.sellPrice || this.contest.prize)
    );
  }

  isImage(filename: string) {
    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return CONTEST_ENTRY_IMAGE_FORMATS.includes(fileType);
  }

  isVideo(filename: string) {
    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return CONTEST_ENTRY_VIDEO_FORMATS.includes(fileType);
  }

  isEntryOwner() {
    return this.userId === this.entry.ownerId;
  }

  showErrorsOnHeader() {
    const headerEntryActions = [
      EntryAction.RATE,
      EntryAction.REJECT,
      EntryAction.RECONSIDER,
    ];
    return headerEntryActions.includes(this.entryAction);
  }

  onRateEntry(changes: Partial<ContestViewEntry>) {
    this.entryAction = EntryAction.RATE;
    this.updateResponse$ = this.updateEntry(changes);
  }

  onRejectEntry(changes: Partial<ContestViewEntry>) {
    this.entryAction = EntryAction.REJECT;
    this.rejectResponse$ = this.updateEntry(changes);
    this.updateResponse$ = this.rejectResponse$;
  }

  onReconsiderEntry(changes: Partial<ContestViewEntry>) {
    this.entryAction = EntryAction.RECONSIDER;
    this.reconsiderResponse$ = this.updateEntry(changes);
    this.updateResponse$ = this.reconsiderResponse$;
  }

  onLikeEntry(changes: Partial<ContestViewEntry>) {
    this.entryAction = this.entry.isLiked
      ? EntryAction.DISLIKE
      : EntryAction.LIKE;
    this.updateResponse$ = this.updateEntry(changes);
  }

  updateEntry(changes: Partial<ContestViewEntry>) {
    return this.datastore
      .document('contestViewEntries', this.entry.id)
      .update(changes);
  }

  handleChatClick() {
    this.modalRef.close(PreviewModalResponse.START_CHAT);
  }

  ngOnDestroy() {
    this.ratingSubscription.unsubscribe();
  }
}
